import { useState } from 'react';
import { API } from '../config/config';
import { useAuth } from './useAuth';
import {
  GetReferralCodesParams, PaginatedResponse,
  ReferralCodeRequest,
  ReferralCodeResponse,
  ReferralCodeStatusUpdateRequest
} from '../content/applications/Referral/referral.interface';



export const useReferral = () => {
  const { enhancedFetch } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = () => setError(null);

  // POST `/` — Create referral code
  const createReferralCode = async (
    req: ReferralCodeRequest
  ): Promise<ReferralCodeResponse | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await enhancedFetch(
        `${API.HOST}/api/v1/referral-codes`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(req)
        }
      );
      return res as ReferralCodeResponse;
    } catch (err: any) {
      const msg = err?.message || 'Failed to create referral code';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // PUT `/{id}/status` — Update referral code status
  const updateReferralCodeStatus = async (
    id: string,
    body: ReferralCodeStatusUpdateRequest
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      await enhancedFetch(
        `${API.HOST}/api/v1/referral-codes/${id}/status`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        }
      );
      return true;
    } catch (err: any) {
      const msg = err?.message || 'Failed to update referral code status';
      setError(msg);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // GET `/{id}` — Get referral code by ID
  const getReferralCodeById = async (
    id: string
  ): Promise<ReferralCodeResponse | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await enhancedFetch(
        `${API.HOST}/api/v1/referral-codes/${id}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      return res as ReferralCodeResponse;
    } catch (err: any) {
      const msg = err?.message || 'Failed to fetch referral code';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // GET `/code/{code}` — Get referral code by code
  const getReferralCodeByCode = async (
    code: string
  ): Promise<ReferralCodeResponse | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await enhancedFetch(
        `${API.HOST}/api/v1/referral-codes/code/${encodeURIComponent(code)}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      return res as ReferralCodeResponse;
    } catch (err: any) {
      const msg = err?.message || 'Failed to fetch referral code by code';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // GET `/` — List referral codes (paged)
  const getReferralCodes = async (
    params?: GetReferralCodesParams
  ): Promise<PaginatedResponse<ReferralCodeResponse> | null> => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (params?.page !== undefined) qs.append('page', String(params.page));
      if (params?.size !== undefined) qs.append('size', String(params.size));
      if (params?.sort) qs.append('sort', params.sort);
      if (params?.email) qs.append('email', params.email);

      const url = `${API.HOST}/api/v1/referral-codes${qs.toString() ? `?${qs.toString()}` : ''}`;

      const res = await enhancedFetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      return res as PaginatedResponse<ReferralCodeResponse>;
    } catch (err: any) {
      const msg = err?.message || 'Failed to list referral codes';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // GET `/user/{referrerUserId}` — List by referrer user
  const getReferralCodesByUser = async (
    referrerUserId: string
  ): Promise<ReferralCodeResponse[] | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await enhancedFetch(
        `${API.HOST}/api/v1/referral-codes/user/${referrerUserId}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      // Backend spec says CollectionResponse<ReferralCodeResponse>
      // Try to normalize to array of items/content if wrapped
      if (Array.isArray(res)) return res as ReferralCodeResponse[];
      if (res?.items && Array.isArray(res.items)) return res.items as ReferralCodeResponse[];
      if (res?.content && Array.isArray(res.content)) return res.content as ReferralCodeResponse[];
      return res as ReferralCodeResponse[]; // best-effort fallback
    } catch (err: any) {
      const msg = err?.message || 'Failed to list referral codes by user';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // POST `/request` — Request a referral code (auth required via enhancedFetch)
  const requestReferralCode = async (): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      await enhancedFetch(`${API.HOST}/api/v1/referral-codes/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      return true;
    } catch (err: any) {
      const msg = err?.message || 'Failed to request referral code';
      setError(msg);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    // Actions
    createReferralCode,
    updateReferralCodeStatus,
    getReferralCodeById,
    getReferralCodeByCode,
    getReferralCodes,
    getReferralCodesByUser,
    requestReferralCode,

    // State
    loading,
    error,
    clearError
  };
};
